import * as _ from "lodash";

const linesMaterial = new THREE.LineBasicMaterial({
    color: new THREE.Color(0xFF0000),
    transparent: true,
    depthWrite: false,
    depthTest: true,
    linewidth: 10,
    opacity: 1.0
});

export class NodeAnimator {

    private viewer: Autodesk.Viewing.GuiViewer3D;
    private tree;
    private fragList;

    private nodes: number[] = [];
    private position = 0;
    private fragMatrices: { [key: number]: THREE.Matrix4 } = {};

    private stepIndex: number;
    private steps: { x: number, y: number, z: number }[];
    private t = 0;
    private dt = 0.01;
    private requestId: number;
    private pauseId: number;

    constructor(viewer: Autodesk.Viewing.GuiViewer3D) {
        this.viewer = viewer;

        (viewer.impl as any).createOverlayScene(
            "lines",
            linesMaterial);
    }

    start(nodes: number[], position: number, steps) {
        if (steps) {

            this.tree = this.viewer.model.getInstanceTree();
            this.fragList = this.viewer.model.getFragmentList();

            this.nodes = nodes;
            this.steps = steps;
            this.position = position;
            this.fragMatrices = {};
            for (const node of nodes) {
                this.fragMatrices = { ...this.fragMatrices, ...this.getFragmentWorldMatrixByNodeId(node) };
            }

            this.stepIndex = 0;
            this.t = 0;
            this.dt = 0.01;
            if (!this.requestId) {
                this.loop();
            }
        }
    }

    stop() {
        if (this.requestId) {
            cancelAnimationFrame(this.requestId);
            this.requestId = null;
        }

        if (this.pauseId) {
            window.clearTimeout(this.pauseId);
            this.pauseId = null;
        }

        (this.viewer.impl as any).clearOverlay("lines");
        for (let node of this.nodes) {
            this.resetNodePosition(node);
        }
        this.viewer.impl.sceneUpdated(true);
        this.nodes = [];
        this.steps = null;
    }

    private setNodePosition(node: number, x, y, z) {
        
        this.tree.enumNodeFragments(node,
                (frag) => {
                    this.fragList.updateAnimTransform(frag,
                        null,
                        null,
                        new THREE.Vector3(x, y, z));
                });
    }

    private resetNodePosition(node: number) {
        const tree = this.viewer.model.getInstanceTree();
        const fragList = this.viewer.model.getFragmentList();

        tree.enumNodeFragments(node,
            (frag) => {
                fragList.updateAnimTransform(frag,
                    null,
                    null,
                    null);
            });
    }

    private loop = () => {
        let pause = false;
        (this.viewer.impl as any).clearOverlay("lines");

        const nodesAtEnd: boolean[] = [];

        for (const node of this.nodes) {
            if (!this.fragMatrices[node]) {
                continue;
            }

            const matrix4 = this.fragMatrices[node];
            const vec = new THREE.Vector3();
            vec.setFromMatrixPosition(matrix4);

            const a = { ...this.steps[this.stepIndex] };
            a.x = a.x + this.position * 2000;
            const va = new THREE.Vector3(a.x, a.y, a.z);
            va.sub(vec);
            //a = va;

            let b: { x: number; y: number; z: number };
            if (this.stepIndex < this.steps.length - 1) {
                b = { ...this.steps[this.stepIndex + 1] };
                b.x = b.x + this.position * 2000;

                const vb = new THREE.Vector3(b.x, b.y, b.z);
                vb.sub(vec);
                //b = vb;
            } else {
                const matrix4 = this.fragMatrices[node];
                const vec = new THREE.Vector3();
                vec.setFromMatrixPosition(matrix4);
                b = {
                    x: 0, //vec.x,
                    y: 0, //vec.y,
                    z: 0, //vec.z
                }
            }

            const newX = this.lerp(a.x, b.x, this.ease(this.t)); // interpolate between a and b where
            const newY = this.lerp(a.y, b.y, this.ease(this.t)); // t is first passed through a easing
            const newZ = this.lerp(a.z, b.z, this.ease(this.t)); // function in this example.

            this.addLine([a, b]);

            this.setNodePosition(node, newX, newY, newZ); // set new position
            nodesAtEnd.push(newX === b.x && newY === b.y && newZ === b.z);
        }
        this.t += this.dt;
        //if (this.t <= 0 || this.t >=1) this.dt = -this.dt;        // ping-pong for demo
        if (_.every(nodesAtEnd)) {
            this.t = 0;
            if (this.stepIndex === this.steps.length - 1) {
                this.stepIndex = 0;
                pause = true;
            } else {
                this.stepIndex = this.stepIndex + 1;
            }
        }

        this.viewer.impl.sceneUpdated(true);

        if (pause) {
            this.pauseId = window.setTimeout(this.loop, 2000);
        } else {
            this.requestId = requestAnimationFrame(this.loop);
        }
    }

    private lerp(a: number, b: number, t:number): number {
        return a + (b - a) * t;
    }

    private ease(t: number): number {
        return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
    }

    private getFragmentWorldMatrixByNodeId(nodeId: number): { [key: number]: THREE.Matrix4 } {
        const result = {};

        this.tree.enumNodeFragments(nodeId, (frag) => {

            const fragProxy = (this.viewer.impl as any).getFragmentProxy(this.viewer.model, frag);
            const matrix = new THREE.Matrix4();

            fragProxy.getWorldMatrix(matrix);

            result[nodeId] = matrix;
        });
        return result;
    }

    private addLine(points) {

        const geometry = new THREE.Geometry();

        for (let point of points) {
            geometry.vertices.push(new THREE.Vector3(point.x, point.y, point.z));
        }

        const lines = new THREE.Line(geometry,
            linesMaterial,
            THREE.LinePieces);

        (this.viewer.impl as any).addOverlay("lines", lines);

    }

}